// src/cockpit4/HermesDrawer.tsx
// ============================================
// Hermes 右侧抽屉（唯一 AI 入口 · 抽屉接管版）
//
// 背景：顶部「Hermes 助手」Tab 已移除，对话与 P0/P1/P2 能力合并进此抽屉，
//       避免「右上 Hermes 抽屉」与「Hermes 全屏工作区」双对话重复。
// 结构：
//   - 默认收起（宽度 0），点击呼出（对话态 480px / 功能态 580px）
//   - 内容：对话(HermesChat 主战场) + 功能▾(P0/P1/P2 分组) + 思考链(HermesThinking)
//   - 任务/洞察/产物已分散至 经营中心 / 营销中心 / 内容工厂，不再内嵌，全局去重
// 行为：
//   - 展开时从右侧滑入，收起时滑出；始终挂载保留聊天上下文
//   - 分组下拉选择功能 → 主区切换为对应功能面板，可返回对话
// ============================================

import { motion, AnimatePresence } from "framer-motion";
import { useState, useMemo } from "react";
import {
  Sparkles, PanelRightClose, ChevronDown, ArrowLeft, Cpu, Database, Bell,
  CalendarClock, FileBarChart, Network, Mic, Scale, GraduationCap, ShieldCheck,
  Brain, MessageSquare,
} from "lucide-react";
import HermesChat from "../components/Hermes/HermesChat";
import { useCockpit4Store } from "./store";
import { useHermesMessages } from "../hooks/useHermes";
import HermesThinking from "../components/Cockpit/HermesThinking";
// P0/P1/P2 功能面板（原 HermesWorkspace 独有能力，转移至此）
import HermesMemoryPanel from "./components/HermesMemoryPanel";
import HermesTaskExecutor from "./components/HermesTaskExecutor";
import HermesNotificationCenter from "./components/HermesNotificationCenter";
import HermesScheduleManager from "./components/HermesScheduleManager";
import HermesReportGenerator from "./components/HermesReportGenerator";
import HermesCrossDeptCoordinator from "./components/HermesCrossDeptCoordinator";
import HermesMultimodalInput from "./components/HermesMultimodalInput";
import HermesDecisionSupport from "./components/HermesDecisionSupport";
import HermesLearningGrowth from "./components/HermesLearningGrowth";
import HermesAgentMode from "./components/HermesAgentMode";

// ============================================
// 功能分组（P0/P1/P2 · 原 HermesWorkspace 分组结构）
// ============================================

const FUNC_GROUPS: { key: string; tabs: { id: string; label: string; icon: typeof Brain }[] }[] = [
  {
    key: "P0",
    tabs: [
      { id: "memory", label: "企业记忆", icon: Database },
      { id: "executor", label: "任务执行", icon: Cpu },
      { id: "notification", label: "主动提醒", icon: Bell },
    ],
  },
  {
    key: "P1",
    tabs: [
      { id: "schedule", label: "日程会议", icon: CalendarClock },
      { id: "report", label: "信息汇报", icon: FileBarChart },
      { id: "coordinate", label: "跨部门协调", icon: Network },
      { id: "multimodal", label: "多模态", icon: Mic },
    ],
  },
  {
    key: "P2",
    tabs: [
      { id: "decision", label: "决策支持", icon: Scale },
      { id: "learning", label: "学习成长", icon: GraduationCap },
      { id: "agent_mode", label: "代理人", icon: ShieldCheck },
    ],
  },
];

/** 扁平化所有功能 Tab */
const ALL_TABS = FUNC_GROUPS.flatMap((g) => g.tabs);

const LABEL_MAP: Record<string, string> = Object.fromEntries(ALL_TABS.map((t) => [t.id, t.label]));

// ============================================
// 主组件
// ============================================

export default function HermesDrawer() {
  const hermesDrawerOpen = useCockpit4Store((s) => s.hermesDrawerOpen);
  const setHermesDrawer = useCockpit4Store((s) => s.setHermesDrawer);

  // 功能面板状态：chat 为默认，其余为选中的功能
  const [mode, setMode] = useState<string>("chat");
  // 是否显示思考链（执行中）
  const [showThinking, setShowThinking] = useState(false);
  // 展开的功能分组下拉
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  // 消息 + 路由信息（供 HermesThinking 实时思考链）
  const messages = useHermesMessages();
  const routing = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === "assistant" && (msg.intent || msg.runtime || msg.view)) {
        return {
          intent: msg.intent ? String(msg.intent) : undefined,
          runtime: msg.runtime ? String(msg.runtime) : undefined,
          view: msg.view !== undefined ? String(msg.view) : undefined,
        };
      }
    }
    return { intent: undefined, runtime: undefined, view: undefined };
  }, [messages]);

  const inFunction = mode !== "chat";
  const drawerWidth = inFunction ? 580 : 480;
  const activeTabInfo = ALL_TABS.find((t) => t.id === mode);

  return (
    <AnimatePresence>
      {hermesDrawerOpen && (
        <motion.aside
          key="hermes-drawer"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: drawerWidth, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="relative flex-shrink-0 h-full overflow-hidden border-l border-white/[0.06] bg-[#0a0e1a]/80 backdrop-blur-xl"
        >
          {/* 左侧边缘霓虹光带 */}
          <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-transparent via-purple-500/40 to-transparent pointer-events-none" />

          {/* 内部固定宽度容器 */}
          <div className="flex flex-col h-full" style={{ width: drawerWidth }}>
            {/* ============ 顶部 Header ============ */}
            <div className="flex items-center justify-between px-4 h-12 border-b border-white/[0.06] flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Sparkles size={14} className="text-purple-400" />
                  <motion.span
                    className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-emerald-400"
                    animate={{ opacity: [1, 0.4, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[12px] font-semibold text-white tracking-tight">
                    Hermes
                  </span>
                  <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                    AI Executive Assistant
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-0.5">
                {/* 思考链开关 */}
                <button
                  onClick={() => setShowThinking((v) => !v)}
                  title={showThinking ? "隐藏思考链" : "显示思考链（执行中）"}
                  className={`p-1.5 rounded-md transition-colors ${
                    showThinking ? "text-cyan-300 bg-cyan-500/10" : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.05]"
                  }`}
                >
                  <Brain size={14} />
                </button>
                <button
                  onClick={() => setHermesDrawer(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                  title="收起 Hermes 抽屉"
                >
                  <PanelRightClose size={14} />
                </button>
              </div>
            </div>

            {/* ============ 工具条：对话 + 功能▾ ============ */}
            <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 border-b border-white/[0.06]">
              {/* 返回对话 / 当前模式 */}
              {inFunction ? (
                <button
                  onClick={() => setMode("chat")}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
                >
                  <ArrowLeft size={11} /> 返回对话
                </button>
              ) : (
                <button
                  onClick={() => setMode("chat")}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-mono text-purple-300 bg-purple-500/10 transition-colors"
                >
                  <MessageSquare size={11} /> 对话
                </button>
              )}

              {/* 功能分组下拉 */}
              {(["P0", "P1", "P2"] as const).map((g) => {
                const group = FUNC_GROUPS.find((x) => x.key === g)!;
                const activeInGroup = inFunction && group.tabs.some((t) => t.id === mode);
                return (
                  <div key={g} className="relative">
                    <button
                      onClick={() => setOpenGroup(openGroup === g ? null : g)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono transition ${
                        openGroup === g || activeInGroup
                          ? "bg-white/[0.06] text-white border border-white/[0.12]"
                          : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.04] border border-transparent"
                      }`}
                    >
                      {g}
                      <ChevronDown size={9} className={openGroup === g ? "rotate-180 transition-transform" : "transition-transform"} />
                    </button>

                    <AnimatePresence>
                      {openGroup === g && (
                        <>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="fixed inset-0 z-30"
                            onClick={() => setOpenGroup(null)}
                          />
                          <motion.div
                            initial={{ opacity: 0, y: -4, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -4, scale: 0.98 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full left-0 mt-1 z-40 min-w-[150px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl"
                          >
                            {group.tabs.map((tab) => {
                              const Icon = tab.icon;
                              return (
                                <button
                                  key={tab.id}
                                  onClick={() => {
                                    setMode(tab.id);
                                    setOpenGroup(null);
                                  }}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${
                                    mode === tab.id
                                      ? "text-purple-200 bg-purple-500/10"
                                      : "text-gray-300 hover:bg-purple-500/10 hover:text-purple-200"
                                  }`}
                                >
                                  <Icon size={11} className="text-purple-400" />
                                  <span className="flex-1 text-left">{tab.label}</span>
                                  {mode === tab.id && <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />}
                                </button>
                              );
                            })}
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* ============ 内容区 ============ */}
            {/* 功能面板：返回对话条 + 面板 */}
            {inFunction ? (
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
                <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 border-b border-white/[0.06]">
                  {activeTabInfo && (() => {
                    const Icon = activeTabInfo.icon;
                    return <Icon size={12} className="text-purple-400" />;
                  })()}
                  <span className="text-[11px] font-mono text-purple-300 uppercase tracking-wider">
                    {LABEL_MAP[mode]}
                  </span>
                </div>
                <div className="flex-1 min-h-0">
                  {mode === "memory" && <HermesMemoryPanel />}
                  {mode === "executor" && <HermesTaskExecutor />}
                  {mode === "notification" && <HermesNotificationCenter />}
                  {mode === "schedule" && <HermesScheduleManager />}
                  {mode === "report" && <HermesReportGenerator />}
                  {mode === "coordinate" && <HermesCrossDeptCoordinator />}
                  {mode === "multimodal" && <HermesMultimodalInput />}
                  {mode === "decision" && <HermesDecisionSupport />}
                  {mode === "learning" && <HermesLearningGrowth />}
                  {mode === "agent_mode" && <HermesAgentMode />}
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 flex flex-col">
                {/* 对话主战场 */}
                <div className={showThinking ? "flex-1 min-h-0" : "flex-1 min-h-0"}>
                  <HermesChat />
                </div>
                {/* 思考链（执行中，可折叠） */}
                {showThinking && (
                  <div className="flex-shrink-0 max-h-[40%] min-h-0 border-t border-white/[0.06] flex flex-col">
                    <div className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 border-b border-white/[0.05]">
                      <Brain size={10} className="text-cyan-400" />
                      <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">执行中 · 思考链</span>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto">
                      <HermesThinking
                        intent={routing.intent}
                        runtime={routing.runtime}
                        view={routing.view}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}